// A service in words: amounts of money in no currency, each line of the bill in a sentence, what the lines add up to,
// and the day it was done, kept at midday where the owner is so no time zone moves it to another day.
import type { components } from '@tune/shared';

type Line = components['schemas']['ServiceLine'];

/** An amount as people here read it, in no currency: "1,250.50". */
export function moneyText(value: number): string {
  return value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A number as typed ("1,250.50"): null for nothing, NaN for what isn't one. */
export function typedAmount(value: string): number | null {
  const t = value.trim().replace(/[\s,]/g, '');
  return t === '' ? null : Number(t);
}

/** A line of the bill in a sentence: "Oil filter × 1 (15208-65F0E) · 18.50 each". */
export function lineWords(line: Line): string {
  const quantity = line.quantity === null ? null : line.quantity.toLocaleString('en-AU', { maximumFractionDigits: 2 });
  const what = [
    quantity !== null ? `${line.description} × ${quantity}` : line.description,
    line.partNumber ? `(${line.partNumber})` : '',
  ]
    .filter(Boolean)
    .join(' ');
  return [
    what,
    line.unitCost !== null ? `${moneyText(line.unitCost)}${line.quantity !== null ? ' each' : ''}` : '',
    line.labourCost !== null ? `labour ${moneyText(line.labourCost)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** What lines as typed add up to: each unit cost times its quantity (one, if none), and labour. */
export function linesTotal(lines: ReadonlyArray<{ quantity: string; unitCost: string; labourCost: string }>): number {
  const n = (v: string) => {
    const x = typedAmount(v);
    return x !== null && Number.isFinite(x) ? x : null;
  };
  return lines.reduce((sum, l) => sum + (n(l.unitCost) ?? 0) * (n(l.quantity) ?? 1) + (n(l.labourCost) ?? 0), 0);
}

/** A day from a date field, as midday where the owner is. */
export function noon(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y ?? 2000, (m ?? 1) - 1, d ?? 1, 12).toISOString();
}

/** A time as a date field holds it, by the owner's own calendar: "2026-10-08". */
export function dateField(iso: string | number): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
